import { Box, Button, Form, Icon, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import {
  AtsJobRequirementsOptionsEnum,
  UpdateOpenApplicationSettingDocument,
} from 'generated/resources/graphql'

import SelectField from 'modules/core/components/SelectField'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface OpenAapplicationSettingForm {
  id: string
  cvRequirement: AtsJobRequirementsOptionsEnum
  phoneRequirement: AtsJobRequirementsOptionsEnum
  photoRequirement: AtsJobRequirementsOptionsEnum
  personalUrlRequirement: AtsJobRequirementsOptionsEnum
  genderRequirement: AtsJobRequirementsOptionsEnum
}

interface Props {
  openApplicationSetting: OpenAapplicationSettingForm
}

const OpenApplicationForm = ({ openApplicationSetting }: Props) => {
  const [openGenderTooltip, toggleOpenGenderTooltip] = useToggle()
  const [update, { loading }] = useMutation(UpdateOpenApplicationSettingDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsOpenApplicationSetting,
  })

  const requirementTypes: SelectOption[] = [
    {
      label: i18n.t('ats.jobs_form.mandatory'),
      value: 'mandatory',
    },
    {
      label: i18n.t('ats.jobs_form.optional'),
      value: 'optional',
    },
    {
      label: i18n.t('ats.jobs_form.do_not_ask'),
      value: 'do_not_ask',
    },
  ]

  const form = useForm<OpenAapplicationSettingForm>({
    defaultValues: {
      id: openApplicationSetting.id,
      cvRequirement: openApplicationSetting.cvRequirement,
      phoneRequirement: openApplicationSetting.phoneRequirement,
      photoRequirement: openApplicationSetting.photoRequirement,
      personalUrlRequirement: openApplicationSetting.personalUrlRequirement,
      genderRequirement: openApplicationSetting.genderRequirement,
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await update({ variables: data })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return handled
    },
  })

  return (
    <Form onSubmit={() => form.handleSubmit()} padding={null}>
      <Box flexDirection="row" gap="s24">
        <SelectField
          label={i18n.t('ats.settings.candidate_data.open_application_settings.form.cv')}
          options={requirementTypes}
          onChange={(value) => form.setFieldValue('cvRequirement', value)}
          value={form.values.cvRequirement}
        />
        <SelectField
          label={i18n.t('ats.settings.candidate_data.open_application_settings.form.phone')}
          options={requirementTypes}
          onChange={(value) => form.setFieldValue('phoneRequirement', value)}
          value={form.values.phoneRequirement}
        />
      </Box>
      <Box flexDirection="row" gap="s24">
        <SelectField
          label={i18n.t('ats.settings.candidate_data.open_application_settings.form.photo')}
          options={requirementTypes}
          onChange={(value) => form.setFieldValue('photoRequirement', value)}
          value={form.values.photoRequirement}
        />
        <SelectField
          label={i18n.t('ats.settings.candidate_data.open_application_settings.form.personalUrl')}
          options={requirementTypes}
          onChange={(value) => form.setFieldValue('personalUrlRequirement', value)}
          value={form.values.personalUrlRequirement}
        />
      </Box>
      <SelectField
        label={i18n.t('ats.settings.candidate_data.open_application_settings.form.gender')}
        options={requirementTypes}
        onChange={(value) => form.setFieldValue('genderRequirement', value)}
        value={form.values.genderRequirement}
        secondaryLabel={
          <Tooltip.Alt
            open={openGenderTooltip}
            toggle={toggleOpenGenderTooltip}
            label={i18n.t('ats.jobs_form.gender_information')}
            attachment="bottom center"
          >
            <Icon.Small icon="question" encircled="grey800" />
          </Tooltip.Alt>
        }
      />
      {form.dirty && (
        <Form.Buttons>
          <Button disabled={loading} submit>
            {i18n.t('common.submit')}
          </Button>
        </Form.Buttons>
      )}
    </Form>
  )
}

export default OpenApplicationForm
